import React, { useState, useEffect } from "react";
import { Modal, DatePicker, Input, Button, Space, Descriptions } from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

// Extend dayjs with the plugins
dayjs.extend(isSameOrBefore);

interface HoldResumeType {
  id?: string;
  current_exp: string;
  hold_from: string;
  hold_to: string;
  num_days: number;
  new_exp?: string;
}

interface UserData {
  user: {
    id: string;
    end_date: string;
  };
  holdResume?: HoldResumeType;
  office: {
    id: string;
  };
}

interface PauseResumeModalProps {
  data: UserData;
  isModalOpen: boolean;
  handleCancel: () => void;
  onSubmit: (from: Dayjs, to: Dayjs, days: number) => void;
  holdResume?: HoldResumeType;
  isLoading: boolean;
  isPaused: boolean;
}

const PauseResumeModal: React.FC<PauseResumeModalProps> = ({
  data,
  isModalOpen,
  handleCancel,
  onSubmit,
  holdResume,
  isLoading,
  isPaused,
}) => {
  const [from, setFrom] = useState<Dayjs>(dayjs());
  const [to, setTo] = useState<Dayjs>(dayjs().add(1, "day"));
  const [days, setDays] = useState<number>(1);

  // Reset dates when modal opens
  useEffect(() => {
    if (isModalOpen && !isPaused) {
      setFrom(dayjs());
      setTo(dayjs().add(1, "day"));
      setDays(1);
    }
  }, [isModalOpen, isPaused]);

  // Update days when dates change
  useEffect(() => {
    if (from && to) {
      const diffDays = to.startOf("day").diff(from.startOf("day"), "day");
      setDays(diffDays || 1);
    }
  }, [from, to]);

  const handleFromChange = (date: Dayjs | null) => {
    if (date) {
      setFrom(date);
      // When from date changes, set to date to next day
      setTo(date.add(1, "day"));
    }
  };

  const handleToChange = (date: Dayjs | null) => {
    if (date) {
      setTo(date);
    }
  };

  const handleDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDays = parseInt(e.target.value) || 1;
    setDays(newDays);
    if (from) {
      setTo(from.add(newDays, "day"));
    }
  };

  const handleSubmit = () => {
    onSubmit(from, to, days);
  };

  const disabledFromDate = (current: Dayjs) => {
    // Can't select dates before today
    const today = dayjs().startOf("day");
    const userEndDate = dayjs(data?.user?.end_date).startOf("day");

    return (
      current.isBefore(today) || (current && current.isAfter(userEndDate)) // Can't select dates after subscription end
    );
  };

  const disabledToDate = (current: Dayjs) => {
    // Can't select dates before or equal to from date
    return current && (current.isBefore(from) || current.isSame(from, "day"));
  };

  return (
    <Modal
      title={`${isPaused ? "Resume" : "Pause"} Subscription`}
      open={isModalOpen}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={isLoading}
          onClick={handleSubmit}
          disabled={!isPaused && days < 1}
        >
          {isPaused ? "Resume Now" : "Pause Now"}
        </Button>,
      ]}
    >
      {isPaused && holdResume ? (
        <Descriptions column={1}>
          <Descriptions.Item label="Subscription Expiry Date">
            {dayjs(holdResume.current_exp).format("DD-MM-YYYY")}
          </Descriptions.Item>
          <Descriptions.Item label="Hold Period">
            {dayjs(holdResume.hold_from).format("DD-MM-YYYY")} -{" "}
            {dayjs(holdResume.hold_to).format("DD-MM-YYYY")}
          </Descriptions.Item>
          <Descriptions.Item label="Extended Expiry Date">
            {dayjs(holdResume.current_exp)
              .add(holdResume.num_days, "day")
              .format("DD-MM-YYYY")}
          </Descriptions.Item>
          <Descriptions.Item label="No. days">
            {holdResume.num_days} day{holdResume.num_days > 1 ? "s" : ""}
          </Descriptions.Item>
        </Descriptions>
      ) : (
        <Space direction="vertical" style={{ width: "100%" }} size="large">
          <div>
            <div style={{ marginBottom: 8 }}>Hold on</div>
            <DatePicker
              value={from}
              onChange={handleFromChange}
              disabledDate={disabledFromDate}
              style={{ width: "100%" }}
              format="DD-MM-YYYY"
            />
          </div>
          <div>
            <div style={{ marginBottom: 8 }}>Resume on</div>
            <DatePicker
              value={to}
              onChange={handleToChange}
              disabledDate={disabledToDate}
              style={{ width: "100%" }}
              format="DD-MM-YYYY"
            />
          </div>
          <div>
            <div style={{ marginBottom: 8 }}>Total Number of days</div>
            <Input
              type="number"
              value={days}
              onChange={handleDaysChange}
              min={1}
            />
          </div>
        </Space>
      )}
    </Modal>
  );
};

export default PauseResumeModal;
